import { useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { computeWB, taxiFuelL } from "@/lib/navcalc";
import { fmt, FUEL_DENSITY } from "@/lib/units";
import WBDetailTable from "./WBDetailTable";
import WBChart from "./WBChart";
import { Scale, CheckCircle2, AlertTriangle, UserRound, Users } from "lucide-react";

const StatePill = ({ label, wb, mtow }) => {
  const ok = wb.ok;
  return (
    <div className={`rounded-xl border p-4 ${ok ? "border-emerald-500/30 bg-emerald-500/10" : "border-rose-500/30 bg-rose-500/10"}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider text-slate-400">{label}</span>
        {ok ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <AlertTriangle className="h-4 w-4 text-rose-400" />}
      </div>
      <div className="mt-2 font-mono-data text-xl font-bold text-slate-100">{fmt(wb.weight)} kg</div>
      <div className="font-mono-data text-xs text-slate-400">CG {fmt(wb.cg, 3)} m {mtow && wb.weight > mtow ? "· > MTOW" : ""}</div>
    </div>
  );
};

const MarginPill = ({ label, mtow, weight }) => {
  const has = Number(mtow) > 0;
  const margin = has ? mtow - weight : 0;
  const ok = margin >= 0;
  return (
    <div data-testid="wb-margin-pill" className={`rounded-xl border p-4 ${ok ? "border-emerald-500/30 bg-emerald-500/10" : "border-rose-500/30 bg-rose-500/10"}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider text-slate-400">{label}</span>
        {ok ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <AlertTriangle className="h-4 w-4 text-rose-400" />}
      </div>
      <div className={`mt-2 font-mono-data text-xl font-bold ${ok ? "text-emerald-300" : "text-rose-300"}`}>{has ? `${margin >= 0 ? "+" : ""}${fmt(margin)} kg` : "—"}</div>
      <div className="font-mono-data text-xs text-slate-400">{has ? (ok ? "sous MTOW" : "MTOW dépassée") : `MTOW ${fmt(mtow)} kg`}</div>
    </div>
  );
};

export default function WeightBalanceTab({ briefing, setBriefing, aircraft, nav }) {
  const stations = aircraft?.stations || [];
  const firstStation = stations[0]?.name;

  const front = briefing.frontSeat || {};
  const pilotW = Number(front.pilot ?? briefing.pilotWeight ?? 0) || 0;
  const paxW = Number(front.pax ?? 0) || 0;
  const frontTotal = pilotW + paxW;

  // Keep weights[firstStation] in sync with pilot + front passenger (for calc, PDF, shared view)
  useEffect(() => {
    if (!firstStation) return;
    const cur = Number(briefing.weights?.[firstStation]) || 0;
    if (cur !== frontTotal) {
      setBriefing((b) => ({ ...b, weights: { ...b.weights, [firstStation]: frontTotal } }));
    }
    // eslint-disable-next-line
  }, [firstStation, frontTotal]);

  const weights = { ...(briefing.weights || {}), ...(firstStation ? { [firstStation]: frontTotal } : {}) };
  const fuelStartL = Number(briefing.fuelStartL) || 0;
  const wb = computeWB(aircraft, weights, fuelStartL, taxiFuelL(briefing.fuel, aircraft?.fuelBurn), nav.totalFuel);

  const setWeight = (name, v) =>
    setBriefing((b) => ({ ...b, weights: { ...b.weights, [name]: v === "" ? "" : Number(v) } }));
  const setFuelStart = (v) => setBriefing((b) => ({ ...b, fuelStartL: v === "" ? "" : Number(v) }));

  const setFront = (key, v) => {
    const val = v === "" ? 0 : Number(v);
    setBriefing((b) => {
      const nf = { pilot: Number(b.pilotWeight) || 0, pax: 0, ...(b.frontSeat || {}), [key]: val };
      const total = (Number(nf.pilot) || 0) + (Number(nf.pax) || 0);
      return { ...b, frontSeat: nf, weights: { ...b.weights, ...(firstStation ? { [firstStation]: total } : {}) } };
    });
  };
  const applyPilotWeight = () => setFront("pilot", Number(briefing.pilotWeight) || 0);

  return (
    <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
      <div className="space-y-5">
        <div className="glass rounded-2xl p-6">
          <h3 className="mb-4 flex items-center gap-2 font-display text-sm font-semibold uppercase tracking-wide text-cyan-400">
            <Scale className="h-4 w-4" /> Chargement
          </h3>
          {briefing.pilotName && (
            <div data-testid="pilot-weight-banner" className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-cyan-500/30 bg-cyan-500/10 px-4 py-2.5">
              <span className="text-sm text-cyan-200">
                Pilote : <span className="font-semibold">{briefing.pilotName}</span> · {briefing.pilotWeight || 0} kg
              </span>
              <button onClick={applyPilotWeight} data-testid="apply-pilot-weight"
                className="rounded-lg border border-cyan-500/40 px-3 py-1 text-xs font-medium text-cyan-300 hover:bg-cyan-500/20">
                Utiliser ce poids
              </button>
            </div>
          )}
          <div className="space-y-3">
            <div className="flex items-center justify-between rounded-lg bg-slate-900/40 px-3 py-2 text-sm">
              <span className="text-slate-400">Masse à vide</span>
              <span className="font-mono-data text-slate-200">{aircraft?.emptyWeight || 0} kg @ {aircraft?.emptyArm || 0} m</span>
            </div>

            {firstStation && (
              <div className="rounded-lg border border-cyan-500/20 bg-slate-900/30 p-3">
                <div className="mb-2 flex items-center justify-between">
                  <Label className="text-sm text-cyan-300">{firstStation} <span className="text-slate-600">({stations[0].arm} m)</span></Label>
                  <span className="font-mono-data text-sm font-semibold text-slate-100">{fmt(frontTotal)} kg</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-500"><UserRound className="h-3 w-3" /> Pilote</Label>
                    <Input type="number" value={front.pilot ?? briefing.pilotWeight ?? ""} onChange={(e) => setFront("pilot", e.target.value)}
                      placeholder="0" data-testid="wb-front-pilot"
                      className="mt-1 h-9 border-slate-700 bg-slate-900/60 font-mono-data text-slate-100" />
                  </div>
                  <div>
                    <Label className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-500"><Users className="h-3 w-3" /> Passager avant</Label>
                    <Input type="number" value={front.pax ?? ""} onChange={(e) => setFront("pax", e.target.value)}
                      placeholder="0" data-testid="wb-front-pax"
                      className="mt-1 h-9 border-slate-700 bg-slate-900/60 font-mono-data text-slate-100" />
                  </div>
                </div>
              </div>
            )}

            {stations.slice(1).map((s) => (
              <div key={s.name} className="flex items-center gap-3">
                <Label className="flex-1 text-sm text-slate-300">{s.name} <span className="text-slate-600">({s.arm} m)</span></Label>
                <div className="w-32">
                  <Input type="number" value={briefing.weights?.[s.name] ?? ""} onChange={(e) => setWeight(s.name, e.target.value)}
                    placeholder="0" data-testid={`wb-station-${s.name}`}
                    className="h-9 border-slate-700 bg-slate-900/60 font-mono-data text-slate-100" />
                </div>
                <span className="w-6 text-xs text-slate-500">kg</span>
              </div>
            ))}
            <div className="flex items-center gap-3 border-t border-slate-800 pt-3">
              <Label className="flex-1 text-sm text-cyan-300">Carburant départ <span className="text-slate-600">({aircraft?.fuelArm || 0} m)</span></Label>
              <div className="w-32">
                <Input type="number" value={briefing.fuelStartL ?? ""} onChange={(e) => setFuelStart(e.target.value)}
                  placeholder="0" data-testid="wb-fuel-input"
                  className="h-9 border-cyan-500/40 bg-slate-900/60 font-mono-data text-cyan-200" />
              </div>
              <span className="w-6 text-xs text-slate-500">L</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <MarginPill label="Marge masse décollage" mtow={wb.mtow} weight={wb.takeoff.weight} />
          <StatePill label="Décollage" wb={wb.takeoff} mtow={wb.mtow} />
          <StatePill label="Atterrissage" wb={wb.landing} />
        </div>
      </div>

      <div className="glass rounded-2xl p-6">
        <h3 className="mb-4 flex items-center gap-2 font-display text-sm font-semibold uppercase tracking-wide text-cyan-400">
          <Scale className="h-4 w-4" /> Enveloppe de centrage
        </h3>
        <WBChart wb={wb} aircraft={aircraft} briefing={briefing} nav={nav} setBriefing={setBriefing} />
      </div>

      <div className="glass rounded-2xl p-6 xl:col-span-2">
        <h3 className="mb-4 flex items-center gap-2 font-display text-sm font-semibold uppercase tracking-wide text-cyan-400">
          <Scale className="h-4 w-4" /> Détail du calcul <span className="text-slate-500">(carburant à {FUEL_DENSITY} kg/L)</span>
        </h3>
        <WBDetailTable wb={wb} />
      </div>
    </div>
  );
}
